import { Button, Icon, lightColors, UnverifiedIcon, useColorModeValue as mode, useDisclosure } from '@chaine/keychaine'
import React, { memo } from 'react'
import { useParams } from 'react-router-dom'

import { ROUTES } from '../../../../_routes/routes-list'
import { AppCategories, AppType } from '../../../../_shared/types/app-types'
import { useAuth } from '../../../../auth/data/use-auth'
import { apps as allApps } from '../../../../settings/apps/components/apps-data'
import { useReplaceWorkspacenameInURL } from '../../../components'
import { SetupAndVerifyWorkspaceModal } from '../../../components/setup-workspace'
import { CompanyType, Variant } from '../../../components/setup-workspace/types'

/**
 * Interface for how the verification button should be configured
 */
interface IVerificationButtonConfig {
  buttonAction: () => void
  buttonText: string
  showButton: boolean
  useModal: boolean
}

/**
 * Interface for the parameters needed to get the verification button config
 */
interface IGetVerificationButtonConfigParams {
  enabledApps?: { appType: AppType }[]
  isVerified?: boolean
  onOpen: () => void
  workspaceType?: CompanyType
}

/**
 * Utility function to determine the verification button state and actions.
 */
const getVerificationButtonConfig = ({
  enabledApps,
  isVerified,
  onOpen,
  workspaceType
}: IGetVerificationButtonConfigParams): IVerificationButtonConfig => {
  const isCarrier = workspaceType === CompanyType.Carrier

  const eldAppTypes = allApps
    .filter((app) => app.categories?.includes(AppCategories.ELD) && app.appType !== AppType.Orbcomm)
    .map((app) => app.appType)

  const hasEnabledELDApp = Array.isArray(enabledApps) && enabledApps.some((app) => eldAppTypes.includes(app.appType))

  if (isCarrier) {
    if (!isVerified) {
      return {
        buttonAction: () => {
          window.open(ROUTES.CARRIER_ONBOARDING, '_blank')
        },
        buttonText: 'Get verified',
        showButton: true,
        useModal: false
      }
    }
    if (isVerified && !hasEnabledELDApp) {
      return {
        buttonAction: () => {
          window.open(ROUTES.CARRIER_ONBOARDING, '_blank')
        },
        buttonText: 'Connect ELD',
        showButton: true,
        useModal: false
      }
    }
  } else {
    if (!isVerified) {
      return {
        buttonAction: onOpen,
        buttonText: 'Get verified',
        showButton: true,
        useModal: true
      }
    }
  }
  return {
    buttonAction: () => {},
    buttonText: '',
    showButton: false,
    useModal: false
  }
}

/**
 * CompleteVerificationButton component is used to handle workspace verification for different user types.
 */
export const CompleteVerificationButton = memo(() => {
  const { selectedWorkspace, setWorkspace, loadingUserDetails } = useAuth()
  const { handle } = useParams()
  const { replaceWorkspacenameInURL } = useReplaceWorkspacenameInURL()
  const { isOpen, onClose, onOpen } = useDisclosure()
  const hoverBgColor = mode('yellow.300', 'yellow.200')
  const buttonBgColor = mode('yellow.200', 'yellow.100')

  if (loadingUserDetails || !selectedWorkspace) return null

  const { workspace: currentWorkspace, accessType: currentAccessType } = selectedWorkspace

  if (!currentWorkspace) return null

  const {
    displayName = '',
    id = '',
    logo = null,
    workspacename = '',
    workspaceType,
    isVerified,
    enabledApps
  } = currentWorkspace

  const workspaceForModal = {
    displayName,
    id,
    logo,
    workspacename
  }

  const { buttonText, buttonAction, showButton, useModal } = getVerificationButtonConfig({
    enabledApps,
    isVerified,
    onOpen,
    workspaceType
  })

  if (!showButton) return null

  return (
    <>
      <Button
        _hover={{ bg: hoverBgColor }}
        _active={{ bg: hoverBgColor }}
        bg={buttonBgColor}
        aria-label="complete verification button"
        color={lightColors.text.primary}
        leftIcon={<Icon as={UnverifiedIcon} color={lightColors.text.primary} />}
        onClick={buttonAction}
      >
        {buttonText}
      </Button>

      {useModal && isOpen && (
        <SetupAndVerifyWorkspaceModal
          isOpen={isOpen}
          onClose={onClose}
          variant={Variant.VerifyWorkspace}
          onWorkspaceCreation={setWorkspace}
          workspace={workspaceForModal}
          replaceWorkspacenameInURL={!handle ? replaceWorkspacenameInURL : undefined}
          accessType={currentAccessType}
        />
      )}
    </>
  )
})

CompleteVerificationButton.displayName = 'CompleteVerificationButton'
